import React, { useState, useEffect, useCallback, useRef } from 'react';import { Table, Form, Row, Col, Button, ToggleButton, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';import { faSearch, faSort, faSortUp, faSortDown, faStickyNote, faPlus, faEdit } from '@fortawesome/free-solid-svg-icons';import Bill from './Bill';import ShadowBoxTable from '../ShadowBoxTable';import { searchBills, updateBillNotes, deleteBill } from '../../services/BillService';import BillSearchResult from './BillSearchResult';import { useParams } from 'react-router-dom';import { getBankAccounts } from '../../services/BankAccountService';import UpdateNotesModal from './UpdateBillNoteModal';import EditBillModal from '../Requirements/EditBillModal';import PhoneBillCardList from './mobile/PhoneBillCardList';

const formatSearchDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Add a client-side filtering helper function to reuse across the component
const EMPTY_SEARCH_PARAMS = {
  name: '',
  dueStartDate: '',
  dueEndDate: '',
  paidStartDate: '',
  paidEndDate: '',
};

const BillSearch = ({ bankAccountId: propsBankAccountId, showAllBills: propsShowAllBills, mobileLayout, onDelete }) => {
  const { bankAccountId: urlBankAccountId } = useParams();
  const [searchResults, setSearchResults] = useState([]);
  const [searchParams, setSearchParams] = useState({
    name: '',
    dueStartDate: '',
    dueEndDate: '',
    paidStartDate: '',
    paidEndDate: '',
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'due_date',
    direction: 'asc'
  });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBills, setTotalBills] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);
  const BILLS_PER_PAGE = 50;
  
  // Initialize with props or URL param, with props taking precedence
  const initialBankAccountId = propsBankAccountId || (urlBankAccountId ? parseInt(urlBankAccountId, 10) : null);
  const [searchAllAccounts, setSearchAllAccounts] = useState(propsShowAllBills || !initialBankAccountId);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [currentBankAccountId, setCurrentBankAccountId] = useState(initialBankAccountId);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [paidFilter, setPaidFilter] = useState('unpaid'); // 'all', 'paid', or 'unpaid'
  const [isLoading, setIsLoading] = useState(false);

  const searchParamsRef = useRef(searchParams);
  const paidFilterRef = useRef(paidFilter);
  const sortConfigRef = useRef(sortConfig);
  searchParamsRef.current = searchParams;
  paidFilterRef.current = paidFilter;
  sortConfigRef.current = sortConfig;

  // Update currentBankAccountId when props change
  useEffect(() => {
    if (propsBankAccountId !== undefined) {
      setCurrentBankAccountId(propsBankAccountId);
      setSearchAllAccounts(propsShowAllBills);
    }
  }, [propsBankAccountId, propsShowAllBills]);

  // Fetch bank accounts on component mount
  useEffect(() => {
    const fetchBankAccounts = async () => {
      try {
        const accounts = await getBankAccounts();
        setBankAccounts(accounts);
      } catch (error) {
        console.error('Error fetching bank accounts:', error.name);
      }
    };
    
    fetchBankAccounts();
  }, []);
  
  const fetchSearchResults = useCallback(async ({
    page = 1,
    query,
    paid,
    sort,
  } = {}) => {
    try {
      setIsLoading(true);

      const bankAccountIdParam =
        !searchAllAccounts && currentBankAccountId ? currentBankAccountId : null;

      const resolvedQuery = query ?? searchParamsRef.current;
      const resolvedPaid = paid ?? paidFilterRef.current;
      const resolvedSort = sort ?? sortConfigRef.current;

      let isPaidParam = null;
      if (resolvedPaid === 'paid') isPaidParam = true;
      if (resolvedPaid === 'unpaid') isPaidParam = false;

      const result = await searchBills({
        searchTerm: resolvedQuery.name,
        dueStartDate: resolvedQuery.dueStartDate,
        dueEndDate: resolvedQuery.dueEndDate,
        paidStartDate: resolvedQuery.paidStartDate,
        paidEndDate: resolvedQuery.paidEndDate,
        isPaid: isPaidParam,
        sortField: resolvedSort.key,
        sortOrder: resolvedSort.direction,
        bankAccountId: bankAccountIdParam,
        page,
        limit: BILLS_PER_PAGE,
      });

      setSearchResults(result.bills.filter((bill) => bill && bill.id));
      setTotalBills(result.total);
      setHasMorePages(result.hasMore);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error searching bills:', error.name);
    } finally {
      setIsLoading(false);
    }
  }, [searchAllAccounts, currentBankAccountId, BILLS_PER_PAGE]);

  // Load default results when account context or sort changes
  useEffect(() => {
    fetchSearchResults({ page: 1 });
  }, [searchAllAccounts, currentBankAccountId, sortConfig.key, sortConfig.direction, fetchSearchResults]);

  const clearSearch = () => {
    setSearchParams(EMPTY_SEARCH_PARAMS);
    setPaidFilter('unpaid');
    setCurrentPage(1);
    fetchSearchResults({ page: 1, query: EMPTY_SEARCH_PARAMS, paid: 'unpaid' });
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setCurrentPage(1);
    await fetchSearchResults({
      page: 1,
      query: searchParamsRef.current,
      paid: paidFilterRef.current,
      sort: sortConfigRef.current,
    });
  };

  const loadNextPage = async () => {
    if (!hasMorePages) return;
    await fetchSearchResults({ page: currentPage + 1 });
  };

  const loadPrevPage = async () => {
    if (currentPage <= 1) return;
    await fetchSearchResults({ page: currentPage - 1 });
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      if (prevConfig.key === key) {
        return {
          key,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return <FontAwesomeIcon icon={faSort} />;
    return sortConfig.direction === 'asc' ?
      <FontAwesomeIcon icon={faSortUp} /> :
      <FontAwesomeIcon icon={faSortDown} />;
  };

  // Function to get bank account name from id
  const getBankAccountName = (accountId) => {
    if (!accountId) return 'None';
    const account = bankAccounts.find(acc => acc.id === accountId);
    return account ? account.nickname : 'Unknown';
  };

  // Function to handle notes icon click
  const handleNotesClick = (bill) => {
    setSelectedBill(bill);
    setShowNotesModal(true);
  };

  // Function to update bill notes
  const handleUpdateNotes = (updatedBill) => {
    // Update the bill in search results
    setSearchResults(prevResults => 
      prevResults.map(bill => 
        bill.id === updatedBill.id ? updatedBill : bill
      )
    );
    setShowNotesModal(false);
  };

  // Function to handle update button click
  const handleUpdateClick = (bill) => {
    setEditingBill(bill);
    setShowUpdateModal(true);
  };

  // Function to update bill
  const handleBillUpdate = (updatedBill) => {
    if (!updatedBill || !updatedBill.id) {
      console.warn('Received invalid bill update:');
      return;
    }
    
    // Update the bill in the search results
    setSearchResults(prevResults => {
      if (!prevResults) return [];
      
      // Create a new array with the updated bill
      const updatedResults = prevResults.map(bill => 
        bill && bill.id === updatedBill.id ? updatedBill : bill
      ).filter(bill => bill !== null && bill !== undefined);
      
      // Sort the results according to current sort config
      return updatedResults.sort((a, b) => {
        if (sortConfig.key === 'amount') {
          return sortConfig.direction === 'asc' ?
            parseFloat(a[sortConfig.key]) - parseFloat(b[sortConfig.key]) :
            parseFloat(b[sortConfig.key]) - parseFloat(a[sortConfig.key]);
        }
        return sortConfig.direction === 'asc' ?
          String(a[sortConfig.key]).localeCompare(String(b[sortConfig.key])) :
          String(b[sortConfig.key]).localeCompare(String(a[sortConfig.key]));
      });
    });
    
    setShowUpdateModal(false);
    setEditingBill(null);
  };

  // Add a new function to handle bill deletion
  const handleDeleteBill = async (billId, seriesId) => {
    try {
      // Call the API to delete the bill
      await deleteBill(billId, !!seriesId);
      
      // Remove the deleted bill(s) from search results
      if (seriesId) {
        // For series deletion, get the bill with this ID
        const deletedBill = searchResults.find(bill => bill.id === billId);
        if (deletedBill) {
          // Remove all bills in the series with due_date >= this bill's due_date
          setSearchResults(prevResults => 
            prevResults.filter(bill => 
              !(bill.bill_series_id === seriesId && new Date(bill.due_date) >= new Date(deletedBill.due_date))
            )
          );
        }
      } else {
        // For single bill deletion, just remove that bill
        setSearchResults(prevResults => 
          prevResults.filter(bill => bill.id !== billId)
        );
      }
      
      // Refresh the search to update totals
      handleSearch({ preventDefault: () => {} });
    } catch (error) {
      console.error('Error deleting bill:', error.name);
      // You might want to show an error message to the user here
    }
  };

  // Function to get readable paid filter text
  const getPaidFilterText = () => {
    switch(paidFilter) {
      case 'paid': return 'Paid bills only';
      case 'unpaid': return 'Unpaid bills only';
      default: return 'All bills (paid and unpaid)';
    }
  };

  // Add pagination controls to the bottom of the table
  const renderPaginationControls = () => {
    // Calculate total pages
    const totalPages = Math.ceil(totalBills / BILLS_PER_PAGE);
    
    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          Showing page {currentPage} of {totalPages} ({searchResults.length} of {totalBills} bills)
        </div>
        <div>
          <Button 
            variant="outline-primary" 
            onClick={loadPrevPage} 
            disabled={currentPage <= 1 || isLoading}
            className="me-2"
          >
            Previous
          </Button>
          <Button 
            variant="outline-primary" 
            onClick={loadNextPage} 
            disabled={!hasMorePages || isLoading}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  const handleUpdatePaidDateFromSearch = (updatedBill) => {
    setSearchResults((prevResults) =>
      prevResults.map((bill) => (bill.id === updatedBill.id ? updatedBill : bill))
    );
  };

  if (mobileLayout) {
    return (
      <div className="phone-search">
        <Form onSubmit={handleSearch} className="phone-search-form">
          <div className="phone-search-card">
            <Form.Group className="mb-3">
              <Form.Label className="phone-search-label">Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Name, notes, account…"
                value={searchParams.name}
                onChange={(e) => setSearchParams({ ...searchParams, name: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="phone-search-label">Paid status</Form.Label>
              <div className="phone-search-pills">
                {[
                  { value: 'all', label: 'All' },
                  { value: 'paid', label: 'Paid' },
                  { value: 'unpaid', label: 'Unpaid' },
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    className={`phone-search-pill${paidFilter === value ? ' phone-search-pill--active' : ''}`}
                    onClick={() => setPaidFilter(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Form.Group>

            <div className="phone-search-date-block">
              <div className="phone-search-label">Due date</div>
              <div className="phone-search-date-row">
                <Form.Control
                  type="date"
                  aria-label="Due from"
                  value={searchParams.dueStartDate}
                  onChange={(e) => setSearchParams({ ...searchParams, dueStartDate: e.target.value })}
                />
                <span className="phone-search-date-sep">to</span>
                <Form.Control
                  type="date"
                  aria-label="Due to"
                  value={searchParams.dueEndDate}
                  onChange={(e) => setSearchParams({ ...searchParams, dueEndDate: e.target.value })}
                />
              </div>
            </div>

            <div className="phone-search-date-block">
              <div className="phone-search-label">Paid date</div>
              <div className="phone-search-date-row">
                <Form.Control
                  type="date"
                  aria-label="Paid from"
                  value={searchParams.paidStartDate}
                  onChange={(e) => setSearchParams({ ...searchParams, paidStartDate: e.target.value })}
                />
                <span className="phone-search-date-sep">to</span>
                <Form.Control
                  type="date"
                  aria-label="Paid to"
                  value={searchParams.paidEndDate}
                  onChange={(e) => setSearchParams({ ...searchParams, paidEndDate: e.target.value })}
                />
              </div>
            </div>

            <div className="phone-search-actions">
              <Button type="submit" variant="primary" disabled={isLoading}>
                Search
              </Button>
              <Button type="button" variant="outline-secondary" onClick={clearSearch}>
                Clear
              </Button>
            </div>
          </div>
        </Form>

        {searchResults.length > 0 && (
          <p className="phone-search-summary">
            {totalBills} bill{totalBills === 1 ? '' : 's'} · {getPaidFilterText()}
          </p>
        )}

        {isLoading ? (
          <p className="phone-bills-empty">Searching…</p>
        ) : (
          <PhoneBillCardList
            bills={searchResults}
            onUpdatePaidDate={handleUpdatePaidDateFromSearch}
            bankAccountId={currentBankAccountId}
            onDelete={onDelete}
            showAllBills={searchAllAccounts}
            bankAccounts={bankAccounts}
            emptyMessage="No bills match your search."
          />
        )}

        {searchResults.length > 0 && (
          <div className="phone-bills-load-more">{renderPaginationControls()}</div>
        )}

        {selectedBill && (
          <UpdateNotesModal
            show={showNotesModal}
            handleClose={() => setShowNotesModal(false)}
            bill={selectedBill}
            onUpdateNotes={handleUpdateNotes}
          />
        )}

        {editingBill && (
          <EditBillModal
            show={showUpdateModal}
            onHide={() => setShowUpdateModal(false)}
            bill={editingBill}
            onSuccess={handleBillUpdate}
            onDelete={handleDeleteBill}
          />
        )}
      </div>
    );
  }

  return (
    <div>
      <br />
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <h2 style={{ fontSize: "24px", fontWeight: "bold", borderBottom: "2px solid #A0C2BB", display: "inline-block", paddingBottom: "10px" }}>
          <FontAwesomeIcon icon={faSearch} style={{ marginRight: "10px" }} />
          Search Bills
        </h2>
        {currentBankAccountId && !searchAllAccounts && (
          <p style={{ color: "#5c7272", marginTop: "10px" }}>
            Searching in: {getBankAccountName(currentBankAccountId)}
          </p>
        )}
        {searchAllAccounts && (
          <p style={{ color: "#5c7272", marginTop: "10px" }}>
            Searching across all bank accounts
          </p>
        )}
      </div>

      <Form onSubmit={handleSearch}>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Search Bills</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search by name, description, notes or account"
                value={searchParams.name}
                onChange={(e) => setSearchParams({...searchParams, name: e.target.value})}
              />
            </Form.Group>
          </Col>
          <Col md={8}>
            <Form.Group>
              <Form.Label>Paid Status</Form.Label>
              <div className="d-flex">
                <Form.Check
                  type="radio"
                  id="paid-all"
                  label="All Bills"
                  name="paidFilter"
                  value="all"
                  checked={paidFilter === 'all'}
                  onChange={() => setPaidFilter('all')}
                  className="me-3"
                />
                <Form.Check
                  type="radio"
                  id="paid-yes"
                  label="Paid Only"
                  name="paidFilter"
                  value="paid"
                  checked={paidFilter === 'paid'}
                  onChange={() => setPaidFilter('paid')}
                  className="me-3"
                />
                <Form.Check
                  type="radio"
                  id="paid-no"
                  label="Unpaid Only"
                  name="paidFilter"
                  value="unpaid"
                  checked={paidFilter === 'unpaid'}
                  onChange={() => setPaidFilter('unpaid')}
                />
              </div>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Due Date Range</Form.Label>
              <Form.Control
                type="date"
                value={searchParams.dueStartDate}
                onChange={(e) => setSearchParams({...searchParams, dueStartDate: e.target.value})}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>To</Form.Label>
              <Form.Control
                type="date"
                value={searchParams.dueEndDate}
                onChange={(e) => setSearchParams({...searchParams, dueEndDate: e.target.value})}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Paid Date Range</Form.Label>
              <Form.Control
                type="date"
                value={searchParams.paidStartDate}
                onChange={(e) => setSearchParams({...searchParams, paidStartDate: e.target.value})}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>To</Form.Label>
              <Form.Control
                type="date"
                value={searchParams.paidEndDate}
                onChange={(e) => setSearchParams({...searchParams, paidEndDate: e.target.value})}
              />
            </Form.Group>
          </Col>
          <Col md={6} className="d-flex align-items-end gap-2">
            <Form.Group>
              <Form.Control
                type="submit"
                value="Search"
                className="btn btn-primary"
              />
            </Form.Group>
            <Form.Group>
              <Form.Control
                type="button"
                value="Clear"
                className="btn btn-secondary"
                onClick={clearSearch}
              />
            </Form.Group>
            <Form.Group className="d-flex align-items-center">
              <Form.Check
                type="switch"
                id="search-all-accounts"
                label="Search in all bank accounts"
                checked={searchAllAccounts}
                onChange={() => setSearchAllAccounts(!searchAllAccounts)}
                className="ms-3"
                disabled={!currentBankAccountId}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>

      {/* Search summary */}
      {searchResults.length > 0 && (
        <div className="search-summary mb-3 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
          <h6 className="mb-1">Search Results</h6>
          <p className="mb-1 small">
            <strong>Found:</strong> {totalBills} bills
            {!searchAllAccounts && currentBankAccountId && 
              ` in ${getBankAccountName(currentBankAccountId)}`}
            {searchAllAccounts && ' across all accounts'}
          </p>
          <p className="mb-1 small">
            <strong>Showing:</strong> {getPaidFilterText()}
            {searchParams.name && `, search term "${searchParams.name}"`}
            {searchParams.dueStartDate && `, due after ${new Date(searchParams.dueStartDate).toLocaleDateString()}`}
            {searchParams.dueEndDate && `, due before ${new Date(searchParams.dueEndDate).toLocaleDateString()}`}
          </p>
          <p className="mb-0 small">
            <strong>Sorted by:</strong> {sortConfig.key} ({sortConfig.direction === 'asc' ? 'ascending' : 'descending'})
          </p>
        </div>
      )}

      <ShadowBoxTable>
        <Table striped style={{ textAlign: "center", verticalAlign: "middle" }}>
          <thead>
            <tr>
              <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                Bill {getSortIcon('name')}
              </th>
              <th onClick={() => handleSort('amount')} style={{ cursor: 'pointer' }}>
                Amount {getSortIcon('amount')}
              </th>
              <th onClick={() => handleSort('due_date')} style={{ cursor: 'pointer' }}>
                Due Date {getSortIcon('due_date')}
              </th>
              <th onClick={() => handleSort('paid_date')} style={{ cursor: 'pointer' }}>
                Date Paid {getSortIcon('paid_date')}
              </th>
              <th onClick={() => handleSort('bank_account')} style={{ cursor: 'pointer' }}>
                Bank Account {getSortIcon('bank_account')}
              </th>
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {searchResults.map(bill => (
              <tr key={bill.id}>
                <td>{bill.name}</td>
                <td>${parseFloat(bill.amount).toFixed(2)}</td>
                <td>{formatSearchDate(bill.due_date)}</td>
                <td>{formatSearchDate(bill.paid_date)}</td>
                <td>{getBankAccountName(bill.bank_account)}</td>
                <td>
                  {bill.notes ? (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id={`tooltip-notes-${bill.id}`}>{bill.notes}</Tooltip>}
                    >
                      <FontAwesomeIcon 
                        icon={faStickyNote} 
                        onClick={() => handleNotesClick(bill)}
                        style={{ cursor: 'pointer' }}
                      />
                    </OverlayTrigger>
                  ) : (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id={`tooltip-add-notes-${bill.id}`}>Add notes</Tooltip>}
                    >
                      <FontAwesomeIcon 
                        icon={faPlus} 
                        onClick={() => handleNotesClick(bill)}
                        style={{ cursor: 'pointer', opacity: 0.6 }}
                      />
                    </OverlayTrigger>
                  )}
                </td>
                <td>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-edit-${bill.id}`}>Edit Bill</Tooltip>}
                  >
                    <Button
                      variant="outline-primary"
                      size="sm"
                      style={{ width: '30px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                      onClick={() => handleUpdateClick(bill)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                  </OverlayTrigger>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ShadowBoxTable>

      {/* Notes Modal */}
      {selectedBill && (
        <UpdateNotesModal 
          show={showNotesModal}
          handleClose={() => setShowNotesModal(false)}
          bill={selectedBill}
          onUpdateNotes={handleUpdateNotes}
        />
      )}

      {/* Update Bill Modal */}
      {editingBill && (
        <EditBillModal
          show={showUpdateModal}
          onHide={() => setShowUpdateModal(false)}
          bill={editingBill}
          onSuccess={handleBillUpdate}
          onDelete={handleDeleteBill}
        />
      )}

      {/* Pagination Controls */}
      {renderPaginationControls()}
    </div>
  );
};

export default BillSearch;